import type { SpreadsheetGridInput } from 'models/SpreadsheetDocument';
import { parseExcelWorkbook } from 'pages/SiteSettingsPage/excelImport';
import { readWorkbookSheets } from './readWorkbookSheets';

export interface WorkbookFileSheet {
  fileName: string;
  sheetName: string;
  /** 여러 시트를 가진 파일만 시트명을 문서 제목에 붙인다. */
  multiSheet: boolean;
  grid: SpreadsheetGridInput;
}

export interface WorkbookFileIssue {
  fileName: string;
  /** 파일 자체를 못 읽으면 비어 있고, 특정 시트만 실패하면 시트명이 들어간다. */
  sheetName?: string;
  message: string;
}

export interface WorkbookFilesImport {
  sheets: WorkbookFileSheet[];
  issues: WorkbookFileIssue[];
}

/** 파일명에서 확장자만 떼어낸 문서 제목 기본값. */
export function fileTitle(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '') || fileName;
}

/**
 * 여러 엑셀/CSV 파일의 모든 시트를 읽는다.
 *
 * 한 파일씩 순서대로 읽어 워크북을 동시에 메모리에 들고 있지 않는다. 한 파일이나
 * 한 시트가 실패해도 나머지는 계속 가져오고, 실패는 사유와 함께 돌려준다 —
 * 여러 개를 한 번에 올리는 흐름에서 한 장 때문에 전부 취소되면 안 된다.
 */
export async function importWorkbookFiles(
  files: File[],
  options: {
    importGrid?: (grid: SpreadsheetGridInput) => SpreadsheetGridInput | string;
    onProgress?: (completed: number, total: number) => void;
    signal?: AbortSignal;
  } = {},
): Promise<WorkbookFilesImport> {
  const { importGrid, onProgress, signal } = options;
  const result: WorkbookFilesImport = { sheets: [], issues: [] };
  onProgress?.(0, files.length);

  for (const [index, file] of files.entries()) {
    if (signal?.aborted) break;
    try {
      const { workbook, sheetNames } = await parseExcelWorkbook(file);
      if (signal?.aborted) break;
      if (sheetNames.length === 0) throw new Error('시트가 없는 파일입니다.');
      const { sheets, issues } = readWorkbookSheets(workbook, sheetNames, importGrid);
      for (const sheet of sheets) {
        result.sheets.push({
          fileName: file.name,
          sheetName: sheet.sheetName,
          multiSheet: sheetNames.length > 1,
          grid: sheet.grid,
        });
      }
      for (const issue of issues) {
        result.issues.push({
          fileName: file.name,
          sheetName: issue.sheetName,
          message: issue.message,
        });
      }
    } catch (error) {
      result.issues.push({
        fileName: file.name,
        message: error instanceof Error ? error.message : '파일 읽기 실패',
      });
    }
    onProgress?.(index + 1, files.length);
  }
  return result;
}

/** 문서 제목: 시트가 하나면 파일명, 여러 개면 파일명 (시트명). */
export function sheetDocumentName(sheet: WorkbookFileSheet): string {
  const base = fileTitle(sheet.fileName);
  return sheet.multiSheet ? `${base} (${sheet.sheetName})` : base;
}
